import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const iconButtonVariants = cva(
  'inline-flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        ghost:
          'text-text-secondary hover:text-text-primary hover:bg-bg-muted dark:text-text-muted dark:hover:text-text-primary dark:hover:bg-bg-elevated focus:ring-primary-500 focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        'ghost-danger':
          'text-text-subtle hover:text-feedback-error hover:bg-feedback-error/10 focus:ring-feedback-error focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        danger:
          'bg-feedback-error text-white hover:bg-action-danger-hover focus:ring-feedback-error focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        primary:
          'bg-action-primary text-white hover:bg-action-primary-hover shadow-md hover:shadow-glow focus:ring-primary-500 focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
        secondary:
          'bg-bg-muted text-text-secondary hover:bg-bg-subtle border border-border-default dark:bg-bg-subtle dark:text-text-primary dark:hover:bg-bg-subtle dark:border-border-strong focus:ring-primary-500 focus:ring-offset-bg-base dark:focus:ring-offset-bg-base',
      },
      size: {
        sm: 'h-8 w-8',
        md: 'h-10 w-10',
        lg: 'h-12 w-12',
      },
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'md',
    },
  },
);

export { iconButtonVariants };

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  // Default to type="button" so an IconButton inside a <form> never submits it
  // implicitly. Callers that want submit behavior must opt in with type="submit".
  ({ className, variant, size, type = 'button', ...props }, ref) => {
    return <button type={type} className={cn(iconButtonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);

IconButton.displayName = 'IconButton';
